import { Button, FormHelperText, FormLabel, Radio, RadioGroup } from "@mui/joy";
import { useState } from "react";

import { useVenueZonesByClimbType } from "@/hooks/routes";
import { useGenerateTargetsMutation } from "@/hooks/targets";
import { useCurrentVenueUuid } from "@/hooks/venues";

export default function GenerateTargetsForm({
  climbType,
  onSuccess,
}: {
  climbType: "BOULDER" | "SPORT";
  onSuccess?: () => void;
}) {
  const [selectedMode, setSelectedMode] = useState<"GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE">(
    "GLOBAL",
  );
  const venueUuid = useCurrentVenueUuid();
  const mutation = useGenerateTargetsMutation(venueUuid);

  const zonesQuery = useVenueZonesByClimbType(venueUuid, climbType);

  const zonesEnabled = !!zonesQuery.data?.length;

  const handleGenerateTargets = () => {
    if (!venueUuid) return;

    mutation.mutate(
      {
        climbType,
        venueUuid,
        mode: selectedMode,
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: () => {
          alert("Failed to generate targets. Please try again or contact support");
        },
      },
    );
  };

  return (
    <>
      <FormLabel
        sx={{
          fontWeight: "bold",
          mb: 1,
          display: "block",
          textAlign: "left",
        }}
      >
        Choose a mode:
      </FormLabel>
      <RadioGroup
        value={selectedMode}
        onChange={(event) => setSelectedMode(event.target.value as typeof selectedMode)}
        sx={{ mb: 3, textAlign: "left" }}
      >
        <Radio value="GLOBAL" label="Gym-wide targets" />
        <FormHelperText>Set a target for each grade across the entire gym.</FormHelperText>
        {zonesEnabled ? (
          <>
            <Radio
              value="BY_ZONE"
              label={climbType === "SPORT" ? "Sector targets" : "Zone targets"}
              sx={{ mt: 1.5 }}
            />
            <FormHelperText>
              Set targets by grade within each {climbType === "SPORT" ? "sector" : "zone"}.
            </FormHelperText>
          </>
        ) : null}
        {climbType === "SPORT" && (
          <>
            <Radio value="BY_ASCENT_STYLE" label="Ascent style targets" sx={{ mt: 1.5 }} />
            <FormHelperText>
              Set targets by grade for each ascent style (lead, toprope, auto -belay).
            </FormHelperText>
          </>
        )}
      </RadioGroup>

      <Button
        size="lg"
        variant="solid"
        color="primary"
        onClick={handleGenerateTargets}
        loading={mutation.isPending}
        loadingPosition="start"
        fullWidth
      >
        Generate targets
      </Button>
    </>
  );
}
